import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Button, Form, Message, Label, Icon } from 'semantic-ui-react';
import { Field, reduxForm } from 'redux-form';
import ValidatedFormInput from 'components/ValidatedFormInput';
import {
  showCreateDiscountRuleModal,
  showEditDiscountRuleModal,
  saveDiscountRule,
  setEditorErrorMessage,
} from 'state/discountRule/actions';
import {
  requiredFieldValidation,
  rangeValidation,
  minValueValidation,
} from 'utils/formUtils';

export const validate = values => {
  const errors = {};

  const discountPercentageRequired = requiredFieldValidation(
    values.discount_percentage,
    'Discount percentage'
  );
  if (discountPercentageRequired) {
    errors.discount_percentage = discountPercentageRequired;
  } else {
    const rangeError = rangeValidation(values.discount_percentage, 1, 100);
    if (rangeError) {
      errors.discount_percentage = rangeError;
    } else if (!Number.isInteger(Number(values.discount_percentage))) {
      errors.discount_percentage = 'Discount percentage must be a whole number';
    }
  }

  const monthsLeftRequired = requiredFieldValidation(
    values.subscription_period_months_left,
    'Months left'
  );
  if (monthsLeftRequired) {
    errors.subscription_period_months_left = monthsLeftRequired;
  } else if (
    !Number.isInteger(Number(values.subscription_period_months_left))
  ) {
    errors.subscription_period_months_left =
      'Months left must be a whole number';
  } else {
    const monthsLeftMinError = minValueValidation(
      values.subscription_period_months_left,
      1,
      'Months left'
    );
    if (monthsLeftMinError)
      errors.subscription_period_months_left = monthsLeftMinError;
  }

  const priorityRequired = requiredFieldValidation(values.priority, 'Priority');
  if (priorityRequired) {
    errors.priority = priorityRequired;
  } else {
    const minError = minValueValidation(values.priority, 1);
    if (minError) {
      errors.priority = minError;
    }
  }

  return errors;
};

class DiscountRuleEditorModal extends React.Component {
  componentDidUpdate(prevProps) {
    const { isOpen, reset } = this.props;
    if (prevProps.isOpen && !isOpen) {
      reset();
    }
  }

  onClose = () => {
    const { dispatch, isEditMode } = this.props;
    dispatch(setEditorErrorMessage(null));
    if (isEditMode) {
      dispatch(showEditDiscountRuleModal(false, null));
    } else {
      dispatch(showCreateDiscountRuleModal(false));
    }
  };

  onSubmit = values => {
    const { dispatch, isEditMode, editingRule } = this.props;

    const payload = {
      discount_percentage: parseFloat(values.discount_percentage),
      priority: parseInt(values.priority, 10),
      subscription_period_months_left: values.subscription_period_months_left,
      sales_types: ['RENEWAL', 'DOWNGRADE_RENEWAL', 'UPGRADE_RENEWAL'],
    };

    if (isEditMode && editingRule) {
      payload.id = editingRule.id;
    }

    dispatch(saveDiscountRule(payload));
  };

  render() {
    const {
      isOpen,
      isEditMode,
      isLoading,
      handleSubmit,
      pristine,
      invalid,
      errorMessage,
      errors,
    } = this.props;

    return (
      <Modal open={isOpen} onClose={this.onClose} size="small">
        <Modal.Header>
          {isEditMode ? 'Edit Renewal Discount' : 'Create Renewal Discount'}
        </Modal.Header>
        <Modal.Content>
          {errorMessage && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{errorMessage}</p>
              {errors && errors.length > 0 && (
                <Message.List>
                  {errors.map(err => (
                    <Message.Item key={err}>{err}</Message.Item>
                  ))}
                </Message.List>
              )}
            </Message>
          )}
          <Form onSubmit={handleSubmit(this.onSubmit)}>
            <Form.Field>
              <label>Discount Type</label>
              <div
                style={{
                  padding: '0.6em 0.8em',
                  background: '#f9fafb',
                  borderRadius: '4px',
                  border: '1px solid rgba(34,36,38,.15)',
                }}
              >
                <Label color="blue">Renewal</Label>
              </div>
            </Form.Field>
            <Form.Field>
              <label>Applies To</label>
              <div
                style={{
                  padding: '0.6em 0.8em',
                  background: '#f9fafb',
                  borderRadius: '4px',
                  border: '1px solid rgba(34,36,38,.15)',
                }}
              >
                <Label color="green">All Base Packages</Label>
              </div>
              <p
                style={{
                  color: '#888',
                  fontSize: '0.85em',
                  marginTop: '0.4em',
                }}
              >
                This discount will apply to all subscription package types
              </p>
            </Form.Field>
            <Form.Field>
              <label>
                <Icon name="calendar outline" />
                Months Left Rule
              </label>
              <Field
                name="subscription_period_months_left"
                component={ValidatedFormInput}
                placeholder="Enter number of months"
                type="number"
                step="1"
                label={{ basic: true, content: 'months' }}
                labelPosition="right"
                required
              />
              <p
                style={{
                  color: '#888',
                  fontSize: '0.85em',
                  marginTop: '0.4em',
                }}
              >
                Discount applies when remaining subscription ≤ this value
              </p>
            </Form.Field>
            <Form.Field>
              <label>Priority</label>
              <Field
                name="priority"
                component={ValidatedFormInput}
                placeholder="Enter priority"
                type="number"
                required
              />
            </Form.Field>
            <Form.Field>
              <label>
                <Icon name="percent" />
                Discount Percentage
              </label>
              <Field
                name="discount_percentage"
                component={ValidatedFormInput}
                placeholder="Enter discount percentage"
                type="number"
                step="1"
                label={{ basic: true, content: '%' }}
                labelPosition="right"
                required
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            primary
            onClick={handleSubmit(this.onSubmit)}
            disabled={pristine || invalid || isLoading}
            loading={isLoading}
          >
            {isEditMode ? 'Update Discount' : 'Create Discount'}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

DiscountRuleEditorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  editingRule: PropTypes.object,
  reset: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.string),
  dispatch: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
};

DiscountRuleEditorModal.defaultProps = {
  editingRule: null,
  errorMessage: null,
  errors: [],
};

const mapStateToProps = (state, ownProps) => {
  const { isEditMode, editingRule } = ownProps;

  let initialValues = {
    discount_percentage: '',
    priority: '',
    subscription_period_months_left: '',
  };

  if (isEditMode && editingRule) {
    initialValues = {
      discount_percentage: editingRule.discount_percentage || '',
      priority: editingRule.priority || '',
      subscription_period_months_left:
        editingRule.subscription_period_months_left || '',
    };
  }

  return {
    initialValues,
    enableReinitialize: true,
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: 'discountRuleEditor',
    validate,
  })(DiscountRuleEditorModal)
);
